/*
 * Product-information tab slider.
 * - Panels live side-by-side in a track; switching slides the track (tap a tab,
 *   swipe with touch, or two-finger trackpad swipe). Mouse drag is deliberately NOT
 *   a slide gesture — it must stay free for text selection. Height eases to the
 *   active panel.
 * - Read More expands the 3-line-clamped description (button only shows if it overflows).
 * - "Why we made this" scrolls to the brand-content slider lower on the page.
 */
if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.ptabs__nav');
      this.tabs = Array.from(this.querySelectorAll('.ptabs__tab'));
      this.panels = Array.from(this.querySelectorAll('.ptabs__panel'));
      this.viewport = this.querySelector('.ptabs__panels');
      this.track = this.querySelector('.ptabs__track');
      this.index = Math.max(0, this.tabs.findIndex((t) => t.classList.contains('is-active')));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.goTo(i));
      });

      // Badges in the Description row jump to another tab (awards → Awards, cert → Safety).
      this.querySelectorAll('[data-ptabs-goto]').forEach((el) => {
        el.addEventListener('click', () => this.goToPanel(el.dataset.ptabsGoto));
      });

      this.setupReadMore();
      this.setupSentenceClamp();
      this.setupNextDayCountdown();
      this.setupDrag();
      this.setupWheel();

      // "Why we made this" scrolls to the brand-content slider — if this product's
      // template doesn't include that section, there's nothing to jump to, so remove
      // the link entirely. (Script is deferred, so the full DOM is available here.)
      const why = this.querySelector('.ptabs-why');
      if (why) {
        const target = document.querySelector(this.dataset.brandContentTarget || '.brand-content.slider');
        if (target) {
          why.addEventListener('click', () => this.scrollToBrandContent());
        } else {
          why.remove();
        }
      }

      // Overflow chevron: only shown while the nav can still scroll right.
      this.moreBtn = this.querySelector('.ptabs__more');
      if (this.moreBtn && this.nav) {
        this.moreBtn.addEventListener('click', () => {
          this.nav.scrollBy({ left: this.nav.clientWidth * 0.6, behavior: 'smooth' });
        });
        const update = () => this.updateOverflowChevron();
        update();
        this.nav.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
      }

      // Position the track + set the viewport height, and keep both correct as the
      // layout settles (late fonts/images) or the window resizes.
      this.goTo(this.index, { animate: false });
      const relayout = () => this.goTo(this.index, { animate: false });
      window.addEventListener('resize', relayout);
      window.addEventListener('load', relayout);
      requestAnimationFrame(relayout);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);

      // Keep the viewport height glued to the active panel as its content reflows
      // (fonts, images, Read More) — the robust source of truth for height.
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
          if (!this.track.classList.contains('is-dragging')) this.setHeight();
        });
        this.panels.forEach((p) => ro.observe(p));
      }
    }

    updateOverflowChevron() {
      if (!this.moreBtn || !this.nav) return;
      const canScrollRight = this.nav.scrollWidth - this.nav.clientWidth - this.nav.scrollLeft > 2;
      this.moreBtn.classList.toggle('is-visible', canScrollRight);
    }

    // Slide to a panel by index (clamped). animate:false snaps with no transition
    // (used for initial layout and resize).
    goTo(index, opts) {
      opts = opts || {};
      index = Math.max(0, Math.min(this.tabs.length - 1, index));
      this.index = index;

      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) this.revealTab(tab);
      });
      this.panels.forEach((panel, i) => {
        const on = i === index;
        panel.classList.toggle('is-active', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      if (opts.animate === false) {
        const tPrev = this.track.style.transition;
        const vPrev = this.viewport.style.transition;
        this.track.style.transition = 'none';
        this.viewport.style.transition = 'none';
        this.setTranslate(0);
        this.setHeight();
        void this.track.offsetHeight; // flush, then restore transitions
        this.track.style.transition = tPrev;
        this.viewport.style.transition = vPrev;
      } else {
        this.setTranslate(0);
        this.setHeight();
      }
    }

    // Jump to a panel by its data-panel name (no-op if that tab isn't present).
    goToPanel(name) {
      const i = this.tabs.findIndex((t) => t.dataset.panel === name);
      if (i >= 0) this.goTo(i);
    }

    // Track transform: rest at the active index, plus a live drag offset in px.
    setTranslate(extraPx) {
      this.track.style.transform =
        'translateX(calc(-100% * ' + this.index + ' + ' + (extraPx || 0) + 'px))';
    }

    // Fractional height rounded UP — offsetHeight rounds to the nearest pixel and can
    // round down, clipping the last element's bottom border under overflow:hidden.
    measure(el) {
      return el ? Math.ceil(el.getBoundingClientRect().height) : 0;
    }

    setHeight() {
      const panel = this.panels[this.index];
      if (!panel || !this.viewport) return;
      const h = this.measure(panel);
      if (h > 0) this.viewport.style.height = h + 'px'; // never collapse to 0
    }

    // Grow the viewport (smoothly, via the CSS height transition) so a taller incoming
    // panel doesn't clip mid-drag; it settles to the exact target height on release.
    growHeightForDrag(dx) {
      const neighbour = this.panels[this.index + (dx < 0 ? 1 : -1)];
      const current = this.panels[this.index];
      const h = Math.max(this.measure(current), this.measure(neighbour));
      if (h && this.viewport) this.viewport.style.height = h + 'px';
    }

    // True if `target` sits inside a child that can actually scroll horizontally
    // (the track overflows but isn't scrollable, so it's skipped).
    insideHorizontalScroller(target) {
      for (let n = target; n && n !== this.viewport; n = n.parentElement) {
        if (n === this.track) continue;
        const ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth - n.clientWidth > 2) return true;
      }
      return false;
    }

    // Trackpad two-finger horizontal swipe → change tabs (the desktop "swipe").
    // Vertical scrolling and a physical mouse wheel (deltaY only) are left alone.
    setupWheel() {
      const vp = this.viewport;
      if (!vp) return;
      let accum = 0;
      let cooling = false;
      let resetTimer = null;

      vp.addEventListener('wheel', (e) => {
        // Only decisively-horizontal gestures qualify; leave vertical scroll to the page.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        if (this.insideHorizontalScroller(e.target)) return; // let the awards row scroll
        e.preventDefault(); // don't also scroll the page sideways
        if (cooling) return;

        accum += e.deltaX;
        clearTimeout(resetTimer);
        resetTimer = setTimeout(() => { accum = 0; }, 200);

        const THRESHOLD = 80;
        if (Math.abs(accum) < THRESHOLD) return;
        this.goTo(this.index + (accum > 0 ? 1 : -1));
        accum = 0;
        cooling = true; // ignore the momentum tail so one swipe = one tab
        setTimeout(() => { cooling = false; }, 500);
      }, { passive: false });
    }

    // Drag/swipe the content to change tabs — works for touch and mouse (Pointer Events).
    setupDrag() {
      const vp = this.viewport;
      if (!vp) return;
      let startX = 0, startY = 0, dx = 0, dy = 0, w = 1, t0 = 0, pid = null;
      let down = false, locked = false;

      vp.addEventListener('pointerdown', (e) => {
        // Mouse drag-across-text is the selection gesture — never hijack it.
        // Desktop swipe is covered by the trackpad wheel handler instead.
        if (e.pointerType === 'mouse') return;
        // Leave gestures that begin inside a genuinely horizontally-scrollable child
        // (e.g. the awards row) to it.
        if (this.insideHorizontalScroller(e.target)) return;
        down = true; locked = false; dx = 0; dy = 0;
        startX = e.clientX; startY = e.clientY;
        w = vp.clientWidth || 1; t0 = e.timeStamp; pid = e.pointerId;
      });

      vp.addEventListener('pointermove', (e) => {
        if (!down) return;
        dx = e.clientX - startX;
        dy = e.clientY - startY;
        if (!locked) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dy) > Math.abs(dx)) { down = false; return; } // vertical → let it scroll
          locked = true;
          this.track.classList.add('is-dragging');
          try { vp.setPointerCapture(pid); } catch (_) {}
          this.growHeightForDrag(dx);
        }
        // Resistance when dragging past the first / last panel.
        let move = dx;
        if ((this.index === 0 && dx > 0) || (this.index === this.tabs.length - 1 && dx < 0)) {
          move = dx * 0.35;
        }
        this.setTranslate(move);
      });

      const end = (e) => {
        if (!down) return;
        down = false;
        this.track.classList.remove('is-dragging');
        if (!locked) return;
        locked = false;
        const dt = e.timeStamp - t0;
        // Commit if dragged far enough, or a quick flick.
        const passed = Math.abs(dx) > w * 0.2 || (Math.abs(dx) > 40 && dt < 300);
        this.goTo(this.index + (passed ? (dx < 0 ? 1 : -1) : 0));
        if (Math.abs(dx) > 6) this.suppressClick();
      };
      vp.addEventListener('pointerup', end);
      vp.addEventListener('pointercancel', end);
    }

    // After a real drag, swallow the click it would otherwise fire (e.g. on a link).
    suppressClick() {
      const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      this.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(() => this.removeEventListener('click', stop, true), 400);
    }

    // Keep the active tab within the scrollable nav.
    revealTab(tab) {
      if (!this.nav) return;
      const navRect = this.nav.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      if (tabRect.left < navRect.left) {
        this.nav.scrollBy({ left: tabRect.left - navRect.left - 16, behavior: 'smooth' });
      } else if (tabRect.right > navRect.right) {
        this.nav.scrollBy({ left: tabRect.right - navRect.right + 16, behavior: 'smooth' });
      }
    }

    // Wire up every Read More (Description and Safety each have one).
    setupReadMore() {
      this.querySelectorAll('.ptabs-desc').forEach((wrap) => {
        const text = wrap.querySelector('[data-ptabs-clamp]');
        const btn = wrap.querySelector('.ptabs-desc__more');
        if (!text || !btn) return;

        // Only surface Read More if the text actually overflows the 3-line clamp.
        if (text.scrollHeight - text.clientHeight > 2) {
          btn.hidden = false;
        }

        btn.addEventListener('click', () => {
          const expanded = text.classList.toggle('is-expanded');
          btn.textContent = expanded ? 'Read Less' : 'Read More';
          this.setHeight(); // text grew/shrank — refit the viewport
        });
      });
    }

    // Read More that truncates at the FIRST SENTENCE (used by Safety). Collapsed shows
    // just the first sentence; expanding swaps back to the full text.
    setupSentenceClamp() {
      this.querySelectorAll('[data-ptabs-sentence]').forEach((wrap) => {
        const text = wrap.querySelector('.ptabs-desc__text');
        const btn = wrap.querySelector('.ptabs-desc__more');
        if (!text || !btn) return;

        const fullHTML = text.innerHTML;
        const plain = (text.textContent || '').replace(/\s+/g, ' ').trim();
        const match = plain.match(/^.*?[.!?](?=\s|$)/); // up to the first sentence end
        if (!match) return;
        const first = match[0].trim();
        if (first.length >= plain.length - 1) return; // only one sentence — nothing to hide

        const collapsedHTML = '<p>' + first + '</p>';
        text.innerHTML = collapsedHTML;
        btn.hidden = false;
        let expanded = false;
        btn.addEventListener('click', () => {
          expanded = !expanded;
          text.innerHTML = expanded ? fullHTML : collapsedHTML;
          btn.textContent = expanded ? 'Read Less' : 'Read More';
          this.setHeight(); // content grew/shrank — refit the viewport
        });
      });
    }

    // Counts down to the daily next-day-delivery cutoff — 3pm UK time. Computed in
    // Europe/London so it's accurate wherever the shopper is, and across BST/GMT.
    setupNextDayCountdown() {
      const el = this.querySelector('[data-nextday-countdown]');
      if (!el) return;
      const CUTOFF_HOUR = 15; // 3pm UK cutoff

      // Current time-of-day in Europe/London, as seconds since midnight there.
      const londonSecondsNow = () => {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London', hour12: false,
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(new Date());
        const val = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
        let h = val('hour');
        if (h === 24) h = 0; // en-GB reports midnight as "24"
        return h * 3600 + val('minute') * 60 + val('second');
      };

      const sub = el.closest('.ptabs-delivery__rowsub');
      const render = () => {
        const remaining = CUTOFF_HOUR * 3600 - londonSecondsNow();
        // Past today's cutoff — drop the countdown line, leaving just "Next Day Delivery"
        const missed = remaining <= 0;
        if (sub && sub.hidden !== missed) {
          sub.hidden = missed;
          this.setHeight(); // row height changed — refit the tab viewport
        }
        if (missed) return;
        const mins = Math.floor(remaining / 60);
        el.textContent = Math.floor(mins / 60) + 'hrs ' + (mins % 60) + 'min';
      };
      render();
      setInterval(render, 30000);
    }

    scrollToBrandContent() {
      const selector = this.dataset.brandContentTarget || '.brand-content.slider';
      const target = document.querySelector(selector);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  customElements.define('product-tabs', ProductTabs);
}

// FAQ tab accordion — one item open at a time; opening an item closes the
// rest. The tab viewport's ResizeObserver picks up the height change.
document.addEventListener('click', function (e) {
  var q = e.target.closest('.ptabs-faq__q');
  if (!q) return;
  var item = q.closest('.ptabs-faq__item');
  var list = q.closest('.ptabs-faq');
  var wasOpen = item.classList.contains('is-open');
  list.querySelectorAll('.ptabs-faq__item.is-open').forEach(function (open) {
    open.classList.remove('is-open');
    open.querySelector('.ptabs-faq__q').setAttribute('aria-expanded', 'false');
  });
  if (!wasOpen) {
    item.classList.add('is-open');
    q.setAttribute('aria-expanded', 'true');
  }
});
